// files-drawer.tsx
import React, { useCallback, useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { FileText, FileSpreadsheet, FileImage, FileType, File, FileVideo, FileAudio, Presentation, Archive, Play } from 'lucide-react';

import {
  Overlay,
  Fullscreen,
  Body,
  MainContent,
} from '../sidebar/sidebar.styled';

import { Header } from '../header/header';
import { useChat } from '../chat.hooks';
import SimpleSearchBar from '../search-bar/search-bar';
import { ThreeDots } from '../reusable-image-components/three-dots';
import { FileOptionsModal } from './file-options-modal';
import { ConfirmDeleteModal } from '../sidebar/notes/modals/copy-delete/confirm-delete/confirm-delete';
import { useConfirmDeleteModal } from '../sidebar/notes/modals/copy-delete/confirm-delete/confirm-delete.hooks';
import { filesApi } from '../../../api/files/files';

import {
  SearchBarRow,
  AnimatedSheet,
  SectionHeader,
  MostRecent,
  NonSearchContainer,
  NonSearchItem,
  FileItemContent,
  FileItemName,
  FileItemMeta,
  FileIconWrap,
  DotsButton,
  SearchSheetInner,
  LoadMoreSentinel,
  LoadingMoreText,
  ThumbnailWrap,
  ThumbnailImg,
  ThumbnailVideo,
  ThumbnailShimmer,
  VideoPlayOverlay,
  FadeInItem,
  Divider,
} from './files-drawer.styled';

import { ImageLightbox } from './image-lightbox';

import {
  type NotesDrawerProps,
  type FileLite,
  useNotesDrawer,
  useFileActions,
  useInfiniteScroll,
  useBlobUrl,
  formatUpdatedAt,
} from './files-drawer.hooks';

import { useAuth } from '../../../contexts/auth/auth-context';

/* ── Kind → Lucide icon map ── */
const ICON_PROPS = { size: 24, color: '#b7bdcb', strokeWidth: 1.5 } as const;

const KindIcon: React.FC<{ kind?: FileLite['kind'] }> = ({ kind }) => {
  switch (kind) {
    case 'pdf':          return <FileType {...ICON_PROPS} />;
    case 'doc':          return <FileText {...ICON_PROPS} />;
    case 'sheet':        return <FileSpreadsheet {...ICON_PROPS} />;
    case 'img':          return <FileImage {...ICON_PROPS} />;
    case 'video':        return <FileVideo {...ICON_PROPS} />;
    case 'audio':        return <FileAudio {...ICON_PROPS} />;
    case 'presentation': return <Presentation {...ICON_PROPS} />;
    case 'archive':      return <Archive {...ICON_PROPS} />;
    case 'text':         return <FileText {...ICON_PROPS} />;
    default:             return <File {...ICON_PROPS} />;
  }
};

/* ── Image thumbnail with blob URL loading ── */
const ImageThumbnail: React.FC<{
  file: FileLite;
  onImageClick?: ((file: FileLite, blobUrl: string) => void) | undefined;
}> = ({ file, onImageClick }) => {
  const { blobUrl, loading, error } = useBlobUrl(file.id);

  return (
    <ThumbnailWrap>
      {loading && <ThumbnailShimmer />}
      {!loading && !error && blobUrl && (
        <ThumbnailImg
          src={blobUrl}
          alt={file.name}
          loading="lazy"
          onClick={() => onImageClick?.(file, blobUrl)}
          style={{ cursor: 'pointer' }}
        />
      )}
      {!loading && error && (
        <div style={{ padding: 16, color: '#b7bdcb', fontSize: 13 }}>
          Failed to load preview
        </div>
      )}
    </ThumbnailWrap>
  );
};

/* ── Video thumbnail with blob URL loading ── */
const VideoThumbnail: React.FC<{ file: FileLite }> = ({ file }) => {
  const { blobUrl, loading, error } = useBlobUrl(file.id);
  const [videoReady, setVideoReady] = useState(false);
  const [playing, setPlaying] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const showShimmer = loading || !videoReady;

  // Handle cached metadata (readyState already >= 1 before handler attached)
  React.useEffect(() => {
    const vid = videoRef.current;
    if (vid && vid.readyState >= 1) setVideoReady(true);
  }, [blobUrl]);

  const handlePlay = () => {
    setPlaying(true);
    const vid = videoRef.current;
    if (vid) {
      vid.currentTime = 0;
      vid.muted = false;
      vid.play().catch(() => {});
    }
  };

  return (
    <ThumbnailWrap>
      {showShimmer && <ThumbnailShimmer />}
      {!error && blobUrl && (
        <ThumbnailVideo
          ref={videoRef}
          src={blobUrl}
          controls={playing}
          preload="metadata"
          muted
          playsInline
          onLoadedMetadata={() => setVideoReady(true)}
          style={{
            position: showShimmer ? 'absolute' : 'relative',
            top: 0,
            left: 0,
            opacity: videoReady ? 1 : 0,
            transition: 'opacity 0.2s ease-in',
            cursor: playing ? undefined : 'pointer',
          }}
          onClick={playing ? undefined : handlePlay}
        />
      )}
      {videoReady && !playing && (
        <VideoPlayOverlay>
          <Play size={36} color="#fff" fill="#fff" style={{ opacity: 0.85 }} />
        </VideoPlayOverlay>
      )}
      {!loading && error && (
        <div style={{ padding: 16, color: '#b7bdcb', fontSize: 13 }}>
          Failed to load preview
        </div>
      )}
    </ThumbnailWrap>
  );
};

/* ── File item row — horizontal: [icon] [name + meta] [dots] ── */
const FileItemRow: React.FC<{
  file: FileLite;
  dateLabel: string;
  senderLabel: string;
  onDotsClick: (file: FileLite, rect: DOMRect) => void;
  onImageClick?: ((file: FileLite, blobUrl: string) => void) | undefined;
  onRowClick?: (file: FileLite) => void;
}> = ({ file, dateLabel, senderLabel, onDotsClick, onImageClick, onRowClick }) => {
  const dotsRef = useRef<HTMLDivElement | null>(null);
  const isImage = file.kind === 'img';
  const isVideo = file.kind === 'video';

  const handleDots = () => {
    if (dotsRef.current) onDotsClick(file, dotsRef.current.getBoundingClientRect());
  };

  return (
    <NonSearchItem>
      <FileIconWrap>
        <KindIcon kind={file.kind} />
      </FileIconWrap>
      <FileItemContent
        onClick={!isImage && !isVideo ? () => onRowClick?.(file) : undefined}
        style={{ cursor: 'pointer' }}
      >
        {isImage
          ? <ImageThumbnail file={file} onImageClick={onImageClick} />
          : isVideo
            ? <VideoThumbnail file={file} />
            : <FileItemName>{file.name}</FileItemName>
        }
        <FileItemMeta>{senderLabel} &bull; {dateLabel}</FileItemMeta>
      </FileItemContent>
      <DotsButton ref={dotsRef} onClick={handleDots}>
        <ThreeDots />
      </DotsButton>
    </NonSearchItem>
  );
};

/* ── Main drawer ── */
export default function NotesDrawer({ open, onClose, onViewFileInConversation, onFileDeleted }: NotesDrawerProps) {
  const { handleNotificationClick, handleProfileClick } = useChat();
  const { profile } = useAuth();

  const {
    bodyRef,
    barRef,
    sheetTop,
    searchActive,
    setSearchActive,
    fileGroups,
    loading,
    loadingMore,
    hasMore,
    loadMore,
    error,
    removeFile,
    searchQuery,
    searchResults,
    searchLoading,
    searchHasMore,
    searchLoadingMore,
    loadMoreSearch,
    handleSearchChange,
  } = useNotesDrawer(open);

  const {
    lightboxFile,
    lightboxUrl,
    handleImageClick,
    closeLightbox,
    optionsFile,
    optionsAnchor,
    handleDotsClick,
    handleDownload,
    closeOptions,
  } = useFileActions();

  const sentinelRef = useInfiniteScroll(hasMore, loadMore);
  const searchSentinelRef = useInfiniteScroll(searchHasMore, loadMoreSearch);

  // Delete confirmation modal
  const fileToDeleteRef = useRef<FileLite | null>(null);

  const confirmDelete = useConfirmDeleteModal({
    onConfirm: async () => {
      const file = fileToDeleteRef.current;
      if (!file) return;
      await filesApi.deleteFile(file.id);
      removeFile(file.id);
      onFileDeleted?.(file.id);
      fileToDeleteRef.current = null;
    },
    permissionError: "You don't have permission to delete this file.",
    genericError: "Failed to delete file. Please try again.",
  });

  const handleDelete = useCallback(() => {
    if (!optionsFile) return;
    fileToDeleteRef.current = optionsFile;
    closeOptions();
    confirmDelete.openModal();
  }, [optionsFile, closeOptions, confirmDelete]);

  const handleRowClick = useCallback((file: FileLite) => {
    if (file.message_id && file.conversation_id) {
      onClose();
      onViewFileInConversation?.(file.message_id, file.conversation_id);
    }
  }, [onClose, onViewFileInConversation]);

  const getSenderLabel = (f: FileLite) => {
    if (f.sender_id && profile?.id && f.sender_id === profile.id) return 'You';
    return f.sender_name || 'You';
  };

  let runningIdx = 0;

  return (
    <Dialog.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Overlay forceMount />
        <Fullscreen forceMount aria-describedby={undefined}>
          <Dialog.Title style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap' }}>
            Files
          </Dialog.Title>
          <Header
            disableSidebar
            showBackButton
            onBackNavigate={onClose}
            onNotificationClick={handleNotificationClick}
            onProfileClick={handleProfileClick}
          />

          <Body ref={bodyRef} style={{ overflow: 'auto' }}>
            <SearchBarRow ref={barRef}>
              <SimpleSearchBar placeholder="Search your files" onActiveChange={setSearchActive} onSearchChange={handleSearchChange} />
            </SearchBarRow>

            <MainContent aria-hidden={searchActive} style={{ overflow: 'visible' }}>
              {!searchActive && (
                <NonSearchContainer>
                  {loading && (
                    <MostRecent>Loading files...</MostRecent>
                  )}
                  {error && (
                    <MostRecent style={{ color: 'red' }}>{error}</MostRecent>
                  )}
                  {!loading && !error && fileGroups.length === 0 && (
                    <MostRecent>No files found</MostRecent>
                  )}
                  {!loading && !error && fileGroups.map((group, gi) => {
                    const startIdx = runningIdx;
                    runningIdx += group.files.length;
                    return (
                      <React.Fragment key={group.label}>
                        <SectionHeader first={gi === 0}>{group.label}</SectionHeader>
                        {gi === 0 && <Divider />}
                        {group.files.map((f, i) => (
                          <FadeInItem key={f.id} delay={(startIdx + i) * 60}>
                            <FileItemRow file={f} dateLabel={formatUpdatedAt(f.updated_at)} senderLabel={getSenderLabel(f)} onDotsClick={handleDotsClick} onImageClick={handleImageClick} onRowClick={handleRowClick} />
                            {gi === 0 && <Divider />}
                          </FadeInItem>
                        ))}
                      </React.Fragment>
                    );
                  })}

                  {hasMore && <LoadMoreSentinel ref={sentinelRef} />}
                  {loadingMore && <LoadingMoreText>Loading more...</LoadingMoreText>}
                </NonSearchContainer>
              )}
            </MainContent>

            <AnimatedSheet active={searchActive} top={sheetTop} aria-hidden={!searchActive}>
              <SearchSheetInner>
                {/* No query yet — prompt */}
                {!searchQuery.trim() && (
                  <MostRecent style={{ padding: '16px 0' }}>
                    Search by file name or extension
                  </MostRecent>
                )}

                {/* Has query, no results, done loading */}
                {searchQuery.trim() && searchResults.length === 0 && !searchLoading && (
                  <MostRecent style={{ padding: '16px 0' }}>
                    No files found
                  </MostRecent>
                )}

                {/* Search results */}
                {searchQuery.trim() && searchResults.map((f, i) => (
                  <FadeInItem key={f.id} delay={i * 60}>
                    <FileItemRow file={f} dateLabel={formatUpdatedAt(f.updated_at)} senderLabel={getSenderLabel(f)} onDotsClick={handleDotsClick} onImageClick={handleImageClick} onRowClick={handleRowClick} />
                  </FadeInItem>
                ))}

                {/* Infinite scroll sentinel for search results */}
                {searchHasMore && <LoadMoreSentinel ref={searchSentinelRef} />}

                {/* Loading indicators */}
                {searchLoading && (
                  <LoadingMoreText>Searching...</LoadingMoreText>
                )}
                {searchLoadingMore && (
                  <LoadingMoreText>Loading more...</LoadingMoreText>
                )}
              </SearchSheetInner>
            </AnimatedSheet>
          </Body>

          <FileOptionsModal
            open={!!optionsFile}
            onClose={closeOptions}
            onDownload={handleDownload}
            onViewInConversation={() => {
              if (optionsFile?.message_id && optionsFile?.conversation_id) {
                closeOptions();
                onClose();
                onViewFileInConversation?.(optionsFile.message_id, optionsFile.conversation_id);
              } else {
                closeOptions();
              }
            }}
            onDelete={handleDelete}
            anchorRect={optionsAnchor}
          />

          <ImageLightbox
            open={!!lightboxFile}
            onClose={closeLightbox}
            imageUrl={lightboxUrl}
            fileName={lightboxFile?.name ?? ''}
          />

          <ConfirmDeleteModal
            open={confirmDelete.open}
            onCancel={confirmDelete.handleCancel}
            onConfirm={confirmDelete.handleConfirm}
            isDeleting={confirmDelete.isDeleting}
            deleteError={confirmDelete.deleteError}
            title="Delete this file?"
            description=""
          />
        </Fullscreen>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
